import type { CustomTemplateBox } from '@lite-workspace/dto-fotoclass-templates';
import type { DrawableUnsupportedData } from '../../interfaces/drawable-data.interface';
import { buildBoxPassthroughMeta, carriedBoxFields } from '../passthrough';
import { rectFromBox, rectToBox } from './rect';
import { mergeRotation } from './rotation';
import type { BoxDecoder, BoxEncoder } from './codec-contract';

/** Backstop for any BoxType outside the known 8 — the future dispatch `default:` branch routes here, never `null`. */
export const decode: BoxDecoder<CustomTemplateBox, DrawableUnsupportedData> = (box, ctx) => {
  const templateMeta = buildBoxPassthroughMeta(box);
  return {
    id: box.id,
    type: 'unsupported',
    ...carriedBoxFields(box),
    order: 0,
    canResize: false,
    sourceBoxType: box.type,
    raw: box,
    rotation: box.rotation,
    naturalAligns: { container: rectFromBox(box.rect, ctx) },
    ...(box.opacity !== undefined && { opacity: box.opacity }),
    ...(box.blendMode !== undefined && { blendMode: box.blendMode }),
    ...(box.visible !== undefined && { visible: box.visible }),
    ...(box.locked !== undefined && { locked: box.locked }),
    ...(templateMeta !== undefined && { templateMeta }),
  };
};

/**
 * `raw` carries every field of the source box the codec cannot type (params of an unknown BoxType included),
 * so it is the base — but it is a SNAPSHOT taken at decode time. The editor moves, rotates, hides and locks
 * an unsupported box like any other, and returning `raw` untouched silently rolled every such edit back on
 * save. The live presentation fields are therefore written over the snapshot; `templateMeta` stays
 * redundant with `raw` and is never consulted.
 */
export const encode: BoxEncoder<DrawableUnsupportedData, CustomTemplateBox> = (drawable, ctx) => {
  const raw = drawable.raw ?? ctx.fail('unsupported box has no raw payload to round-trip');
  return {
    ...raw,
    ...carriedBoxFields(drawable),
    rect: rectToBox(drawable.naturalAligns.container, ctx),
    rotation: mergeRotation(drawable, ctx),
    ...(drawable.opacity !== undefined && { opacity: drawable.opacity }),
    ...(drawable.blendMode !== undefined && { blendMode: drawable.blendMode }),
    ...(drawable.visible !== undefined && { visible: drawable.visible }),
    ...(drawable.locked !== undefined && { locked: drawable.locked }),
  };
};
